import { isNil, isNull } from 'lodash-es';

import {
  ColorPalette,
  ColorPaletteMode,
  ColorToken,
  CustomColorPalette,
  isCustomColorPalette,
} from '../../core/types/color-palette.js';
import { getColorPalette } from '../../core/utils/get-color-palette.js';
import { isValidColorPalette } from '../../core/utils/is-valid-color-palette.js';
import { DEFAULT_SCALE_FALLBACK_COLOR } from '../constants.js';
import type {
  CategoricalBarData,
  Dimension,
  DimensionMetadata,
  WeakMapDimension,
} from '../types/categorical-bar-chart-data.js';
import { CategoricalBarChartData } from '../types/categorical-bar-chart.js';

/**
 * Series entry for ColorRule evaluation.
 * Internal fields track original data for mapping results back to dimensions.
 */
export type SeriesEntry = CategoricalBarChartData & {
  _originalData: CategoricalBarChartData;
  _dimensionKey?: string;
};

interface ColorRuleFor {
  for: (series: SeriesEntry) => ColorToken | null | undefined | ColorPalette;
}

function isColorToken(value: unknown): value is ColorToken {
  return !isNil(value) && typeof value !== 'string';
}

function isColorPalette(value: unknown): value is ColorPalette {
  return typeof value === 'string';
}

function getColorRuleResult(
  hasColorRules: boolean,
  colorRules: ColorRuleFor,
  seriesForColorRule: SeriesEntry[],
  categoryData: CategoricalBarChartData,
  dimensionKey?: string,
): ColorToken | null | undefined | ColorPalette {
  if (!hasColorRules) {
    return null;
  }

  const seriesEntry = seriesForColorRule.find((s) =>
    dimensionKey
      ? s._originalData === categoryData && s._dimensionKey === dimensionKey
      : s._originalData === categoryData && !s._dimensionKey,
  );

  return seriesEntry ? colorRules.for(seriesEntry) : null;
}

function determineColorForDimension(
  colorRuleResult: ColorToken | null | undefined | ColorPalette,
  originalPaletteColor: ColorToken,
  dimensionKey: string,
  paletteColorPosition: number,
): { color: ColorToken; legendColor: ColorToken | undefined } {
  if (isColorToken(colorRuleResult)) {
    return {
      color: colorRuleResult,
      legendColor: originalPaletteColor,
    };
  }

  if (isColorPalette(colorRuleResult)) {
    return {
      color: getColorPalette(
        dimensionKey,
        paletteColorPosition,
        colorRuleResult,
      ),
      legendColor: originalPaletteColor,
    };
  }

  return {
    color: originalPaletteColor,
    legendColor: undefined,
  };
}

export function getDimensionColor({
  colorByDimension,
  categoryColor,
  categoryName,
  paletteColorPosition,
  colorPalette,
  colorPaletteMode,
  colorRuleResult,
}: {
  colorByDimension: Map<string, ColorToken>;
  categoryColor?: string;
  categoryName: string;
  paletteColorPosition: number;
  colorPalette?: ColorPalette | CustomColorPalette;
  colorPaletteMode: ColorPaletteMode;
  colorRuleResult?: ColorToken | null | ColorPalette;
}): ColorToken {
  const cachedColor = colorByDimension.get(categoryName);
  if (cachedColor) {
    return cachedColor;
  }

  if (categoryColor) {
    return { Default: categoryColor };
  }

  const appliedColorRule = colorRuleResult;

  if (typeof appliedColorRule === 'string') {
    return getColorPalette(
      categoryName,
      paletteColorPosition,
      appliedColorRule,
      colorPaletteMode,
    );
  }

  if (!isNull(appliedColorRule) && typeof appliedColorRule === 'object') {
    return appliedColorRule;
  }

  if (colorPalette) {
    return getColorPalette(
      categoryName,
      paletteColorPosition,
      colorPalette,
      colorPaletteMode,
    );
  }

  return { Default: DEFAULT_SCALE_FALLBACK_COLOR };
}

export function handleSingleDimension({
  categoryData,
  rest,
  categoryName,
  categoryColor,
  colorByDimension,
  colorRules,
  colorPalette,
  colorPaletteMode,
  paletteColorPosition,
  uniqueDimensions,
  dimensionMap,
  newCategory,
  seriesForColorRule,
  hasColorRules,
}: {
  categoryData: CategoricalBarChartData;
  rest: Omit<CategoricalBarChartData, 'unit' | 'category' | 'value' | 'color'>;
  categoryName: string;
  categoryColor?: string;
  colorByDimension: Map<string, ColorToken>;
  colorRules: ColorRuleFor;
  colorPalette?: ColorPalette | CustomColorPalette;
  colorPaletteMode: ColorPaletteMode;
  paletteColorPosition: number;
  uniqueDimensions: Set<string>;
  dimensionMap: WeakMapDimension;
  newCategory: CategoricalBarData;
  seriesForColorRule: SeriesEntry[];
  hasColorRules: boolean;
}) {
  const value = categoryData.value as number;
  const dimensionName = categoryName;
  const id = crypto.randomUUID();

  const colorRuleResult = getColorRuleResult(
    hasColorRules,
    colorRules,
    seriesForColorRule,
    categoryData,
  );

  const color = getDimensionColor({
    colorByDimension,
    categoryColor,
    categoryName,
    paletteColorPosition,
    colorPalette,
    colorPaletteMode,
    colorRuleResult,
  });
  colorByDimension.set(dimensionName, color);
  const newDimension: Dimension = {
    ...rest,
    id,
    name: dimensionName,
    value,
  };
  const dimensionMetadata: DimensionMetadata = {
    id,
    name: dimensionName,
    value,
    color,
  };
  uniqueDimensions.add(dimensionName);
  dimensionMap.set(newDimension, dimensionMetadata);
  newCategory.dimensions.push(newDimension);
}

export function handleMultipleDimensions({
  categoryData,
  rest,
  categoryName,
  colorByDimension,
  colorByCategory,
  colorRules,
  colorPalette,
  categoricalColorPalette,
  uniqueDimensions,
  dimensionMap,
  newCategory,
  seriesForColorRule,
  hasColorRules,
}: {
  categoryData: CategoricalBarChartData;
  rest: Omit<CategoricalBarChartData, 'unit' | 'category' | 'value' | 'color'>;
  categoryName: string;
  colorByDimension: Map<string, ColorToken>;
  colorByCategory: Map<string, { [id: string]: ColorToken }>;
  colorRules: ColorRuleFor;
  colorPalette?: ColorPalette | CustomColorPalette;
  categoricalColorPalette: boolean;
  uniqueDimensions: Set<string>;
  dimensionMap: WeakMapDimension;
  newCategory: CategoricalBarData;
  seriesForColorRule: SeriesEntry[];
  hasColorRules: boolean;
}): void {
  Object.entries(categoryData.value).forEach(([dimensionKey, value], index) => {
    const colorRuleCategoryData = getColorRuleResult(
      hasColorRules,
      colorRules,
      seriesForColorRule,
      categoryData,
      dimensionKey,
    );
    const paletteColorPosition = categoricalColorPalette ? index : 0;

    const colorByCategoryName = colorByCategory.get(categoryName);
    const dimensionColor = colorByCategoryName?.[dimensionKey];

    let originalPaletteColor: ColorToken;
    if (dimensionColor) {
      originalPaletteColor = dimensionColor;
    } else if (
      colorPalette &&
      (isValidColorPalette(colorPalette) || isCustomColorPalette(colorPalette))
    ) {
      originalPaletteColor = getColorPalette(
        dimensionKey,
        paletteColorPosition,
        colorPalette,
      );
    } else {
      originalPaletteColor = { Default: DEFAULT_SCALE_FALLBACK_COLOR };
    }

    const { color, legendColor } = determineColorForDimension(
      colorRuleCategoryData,
      originalPaletteColor,
      dimensionKey,
      paletteColorPosition,
    );

    if (!colorByDimension.has(dimensionKey)) {
      colorByDimension.set(dimensionKey, legendColor ?? color);
    }

    const categoryColorByCategory = colorByCategory.get(categoryName) ?? {};
    categoryColorByCategory[dimensionKey] = legendColor ?? color;
    colorByCategory.set(categoryName, categoryColorByCategory);
    const id = crypto.randomUUID();
    const name = dimensionKey;
    const newDimension: Dimension = {
      ...rest,
      id,
      name,
      value,
      category: categoryName,
    };
    const dimensionMetadata: DimensionMetadata = {
      id,
      name,
      value,
      color,
      legendColor,
    };
    uniqueDimensions.add(dimensionKey);
    dimensionMap.set(newDimension, dimensionMetadata);
    newCategory.dimensions.push(newDimension);
  });
}
